import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const COLORS = {
  primary: "#0760ad",
  secondary: "#118a94",
  primaryLight: "#098a48"
};

const DEFAULT_SERVICES = {
  services: [
    {
      id: 1,
      title: "Advanced Lab Testing",
      description: [
        "WarmWellness is equipped with cutting-edge laboratory facilities to provide comprehensive diagnostic testing. From routine blood work to specialized screenings, we ensure precision and reliability in every test.",
        "Our laboratory services include hematology, biochemistry, microbiology, immunology, and molecular diagnostics, all conducted by skilled professionals using state-of-the-art technology. We prioritize accuracy, efficiency, and timely reporting to support your healthcare decisions."
      ],
      buttonText: "Learn About Lab Services",
      buttonColor: COLORS.secondary,
      image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef",
      bgColor: "bg-gray-50"
    },
    {
      id: 2,
      title: "Advanced Radiology & Imaging",
      description: [
        "At WarmWellness, we offer the most advanced radiology and imaging services using cutting-edge technology to obtain detailed diagnostic pictures of your body's structures.",
        "Our comprehensive imaging services include digital X-rays, MRI, CT scans, ultrasound, and mammography. Each test is performed by certified technologists and interpreted by board-certified radiologists to ensure the most accurate results for your care team."
      ],
      buttonText: "View Imaging Services",
      buttonColor: COLORS.primaryLight,
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d",
      bgColor: "bg-white"
    }
  ]
};

const FeaturedServices = () => {
  const [servicesData, setServicesData] = useState(DEFAULT_SERVICES);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchServicesData = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/services/featured-services/`);
        if (response.ok) {
          const data = await response.json();
          setServicesData(data);
        }
      } catch (error) {
        console.error('Error fetching featured services:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchServicesData();
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <>
      {servicesData.services?.map((service, index) => (
        <div key={service.id} className={`py-16 px-4 sm:px-6 lg:px-8 ${service.bgColor}`}>
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            {/* Image on left for even indexes, right for odd */}
            <motion.div
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className={`order-2 ${index % 2 === 0 ? 'md:order-1' : 'md:order-2 bg-white p-6 rounded-xl shadow-md'}`}
            >
              <img 
                src={service.image} 
                alt={service.title} 
                className="w-full h-auto rounded-lg shadow-md"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/800x500?text=Image+Not+Found';
                }}
              />
            </motion.div>
            
            {/* Content on right for even indexes, left for odd */}
            <motion.div
              initial={{ opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className={`order-1 ${index % 2 === 0 ? 'md:order-2 md:pl-8' : 'md:order-1 md:pr-8'}`}
            >
              <h2 className="text-3xl font-bold mb-6" style={{ color: COLORS.primary }}>
                {service.title}
              </h2>
              {service.description.map((paragraph, i) => (
                <p key={i} className="text-gray-700 mb-4">
                  {paragraph}
                </p>
              ))}
       
            </motion.div>
          </div>
        </div>
      ))}
    </>
  );
};

export default FeaturedServices;